// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the instant each frame of a stream stands for, and back.
 *
 * One module for every place that turns a frame number into a date or a
 * date into a frame: the date track and its hover readout, the date
 * readout over the globe, seeking by date (track clicks, lessons, the pin
 * graph), the overlays' pairing, the pins' CSV. Before it existed each of
 * those wrote `start + i × cadence` itself, and that sum is wrong for any
 * stream whose frames are calendar months or years:
 *
 *  - A monthly `.dsa` declares the *mean* Gregorian month (2 629 746 s,
 *    30.44 days) as its cadence. Real months are 28 to 31 days, so
 *    `start + i × cadence` wanders up to two days either side of the
 *    month it is meant to name. An axis stamped on the 1st at 00:00 names
 *    the previous month for about half its frames; one stamped on the 16th
 *    drifts across month ends over 1850–2100. The stream host hides it
 *    today by moving each origin a day or three into its month (ERA5
 *    starts on the 3rd), which keeps the *month* right but leaves times
 *    such as "1991-01-02 08:37" on an annual frame.
 *  - A model run on a 360-day calendar declares a 30-day cadence; the sum
 *    then loses 5.25 days a year against the real calendar and names the
 *    wrong month for most frames between 2015 and 2100.
 *
 * So the step is read for what it is. A cadence within
 * {@link CALENDAR_STEP_TOLERANCE} (6 %) of k calendar months (k = 1, 2,
 * 3, 4, 6) or of a whole number of years steps by **calendar months**
 * from the first frame's own stamp: the day of the month and the time of
 * day are kept (the day clamped to the month's length), so an origin on
 * the 3rd stays on the 3rd for 1 040 frames. Anything shorter (daily,
 * hourly, 15-minute) is a fixed step and is unchanged.
 *
 * Calendars. The `.dsa` (schema 1.7) declares neither a calendar nor
 * per-frame times; the release's `frames.json` has times but is not read
 * here. Both are accepted as inputs so that when the publisher adds them
 * they are honoured: a `360_day` or `noleap` axis steps in that
 * calendar's months and days and is shown on the real calendar (day 30
 * of a model February is shown as the 28th or 29th; model day 31 does
 * not exist). Without a declared calendar the step is the real one, which
 * for a 360-day monthly model gives the right month on every frame.
 *
 * Precision follows the step ({@link FramePrecision}): a yearly axis is
 * read by year, a monthly one by month and year, a daily one by date, a
 * sub-daily one by date and time (UTC). The day and time on a monthly or
 * yearly stamp are the publisher's offset, not data.
 *
 * Date to frame (seeking, a hover on the track, a lesson's saved date)
 * is the inverse of the labels: on a monthly or yearly axis, the frame
 * whose month or year contains the date (so any date in August 2003
 * lands on the frame labelled Aug 2003); on a finer axis, the nearest
 * frame. Everything here is pure arithmetic on numbers, all in UTC.
 */

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** The mean Gregorian year (365.2425 days), the cadence annual streams declare. */
export const MEAN_GREGORIAN_YEAR_MS = 365.2425 * DAY
/** The mean Gregorian month (2 629 746 s), the cadence monthly streams declare. */
export const MEAN_GREGORIAN_MONTH_MS = MEAN_GREGORIAN_YEAR_MS / 12

/**
 * How far a cadence may sit from k calendar months and still be read as
 * them. 6 %: a 30-day (360-day calendar) month is 1.4 % short of the
 * mean Gregorian one, a 28-day "four weeks" is 8 % short and stays a
 * fixed step, and no two of the allowed multiples overlap.
 */
export const CALENDAR_STEP_TOLERANCE = 0.06

/** The month multiples a cadence can be read as; whole years come on top. */
const MONTH_MULTIPLES: readonly number[] = [1, 2, 3, 4, 6]

/**
 * The calendar the axis's stamps are written in, by its CF-convention
 * name family. `standard` is the real (proleptic Gregorian) calendar.
 */
export type FrameCalendarKind = 'standard' | 'noleap' | 'all_leap' | '360_day'

/** A CF `calendar` attribute as one of the kinds; anything unknown is `standard`. */
export function parseCalendarKind(value: unknown): FrameCalendarKind {
  if (typeof value !== 'string') return 'standard'
  switch (value.trim().toLowerCase()) {
    case 'noleap':
    case '365_day':
      return 'noleap'
    case 'all_leap':
    case '366_day':
      return 'all_leap'
    case '360_day':
      return '360_day'
    default:
      return 'standard'
  }
}

/** How a frame's successor is reached. */
export type FrameStep =
  /** Calendar months (12 = a year), from the first frame's own stamp. */
  | { readonly kind: 'months'; readonly months: number }
  /** A fixed number of milliseconds (in the axis's calendar). */
  | { readonly kind: 'fixed'; readonly ms: number }

/** What a frame's label names: its year, its month, its day, or its minute/second. */
export type FramePrecision = 'year' | 'month' | 'day' | 'minute' | 'second'

function meanYearDays(calendar: FrameCalendarKind): number {
  switch (calendar) {
    case 'noleap':
      return 365
    case 'all_leap':
      return 366
    case '360_day':
      return 360
    default:
      return 365.2425
  }
}

/**
 * The step a declared cadence stands for. Months when it is within the
 * tolerance of 1, 2, 3, 4 or 6 of the calendar's mean months, or of a
 * whole number of its mean years; otherwise the cadence itself.
 */
export function stepForCadence(cadenceMs: number, calendar: FrameCalendarKind = 'standard'): FrameStep {
  if (!(cadenceMs > 0) || !Number.isFinite(cadenceMs)) return { kind: 'fixed', ms: cadenceMs }
  const months = cadenceMs / ((meanYearDays(calendar) * DAY) / 12)
  for (const k of MONTH_MULTIPLES) {
    if (Math.abs(months / k - 1) <= CALENDAR_STEP_TOLERANCE) return { kind: 'months', months: k }
  }
  const years = Math.round(months / 12)
  if (years >= 1 && Math.abs(months / (12 * years) - 1) <= CALENDAR_STEP_TOLERANCE) {
    return { kind: 'months', months: 12 * years }
  }
  return { kind: 'fixed', ms: cadenceMs }
}

/** What a label on an axis with this step names. */
export function precisionForStep(step: FrameStep): FramePrecision {
  if (step.kind === 'months') return step.months % 12 === 0 ? 'year' : 'month'
  if (step.ms >= DAY) return 'day'
  if (step.ms >= MINUTE) return 'minute'
  return 'second'
}

/** {@link precisionForStep} of {@link stepForCadence}. */
export function precisionForCadence(cadenceMs: number, calendar: FrameCalendarKind = 'standard'): FramePrecision {
  return precisionForStep(stepForCadence(cadenceMs, calendar))
}

// ---------------------------------------------------------------------------
// Civil dates (UTC), in the real calendar and in a model's
// ---------------------------------------------------------------------------

/** A date by its parts: `m` 0–11, `d` 1-based, `tod` ms into the day. */
export interface Civil {
  readonly y: number
  readonly m: number
  readonly d: number
  readonly tod: number
}

function isGregorianLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0
}

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

function monthDays(calendar: FrameCalendarKind, y: number, m: number): number {
  if (calendar === '360_day') return 30
  if (m !== 1) return MONTH_DAYS[m]!
  if (calendar === 'noleap') return 28
  if (calendar === 'all_leap') return 29
  return isGregorianLeap(y) ? 29 : 28
}

/** `Date.UTC` that also means years 0–99 (which `Date.UTC` maps to 1900–1999). */
function utcMs(y: number, m: number, d: number, tod: number): number {
  const date = new Date(0)
  date.setUTCFullYear(y, m, d)
  return date.getTime() + tod
}

function civilOf(ms: number): Civil {
  const date = new Date(ms)
  const y = date.getUTCFullYear()
  const m = date.getUTCMonth()
  const d = date.getUTCDate()
  return { y, m, d, tod: ms - utcMs(y, m, d, 0) }
}

/** A model date on the real calendar: a day the real month lacks is its last day. */
function realMs(c: Civil): number {
  return utcMs(c.y, c.m, Math.min(c.d, monthDays('standard', c.y, c.m)), c.tod)
}

function floorDiv(a: number, b: number): number {
  return Math.floor(a / b)
}

/** Days from 1970-01-01 to a date in a fixed-length-year calendar. */
function modelDayNumber(calendar: FrameCalendarKind, y: number, m: number, d: number): number {
  let before = 0
  for (let i = 0; i < m; i++) before += monthDays(calendar, y, i)
  return (y - 1970) * meanYearDays(calendar) + before + (d - 1)
}

function modelDateOfDay(calendar: FrameCalendarKind, day: number): { y: number; m: number; d: number } {
  const yearLen = meanYearDays(calendar)
  const y = 1970 + floorDiv(day, yearLen)
  let rest = day - (y - 1970) * yearLen
  let m = 0
  while (m < 11 && rest >= monthDays(calendar, y, m)) {
    rest -= monthDays(calendar, y, m)
    m++
  }
  return { y, m, d: rest + 1 }
}

// ---------------------------------------------------------------------------
// The axis
// ---------------------------------------------------------------------------

/** One stream's frames in time. Build it with {@link createFrameAxis}. */
export interface FrameAxis {
  /** Stamp of frame 0, ms since epoch. */
  readonly startMs: number
  readonly frameCount: number
  /** The declared cadence (nominal: a mean month on a monthly axis). */
  readonly cadenceMs: number
  readonly calendar: FrameCalendarKind
  readonly step: FrameStep
  readonly precision: FramePrecision
  /** Explicit stamps, one per frame, ascending; when present they win over the step. */
  readonly timesMs: readonly number[] | null
  /** Frame 0's stamp as a date in the axis's calendar. */
  readonly origin: Civil
}

export interface FrameAxisInput {
  readonly startMs: number
  readonly frameCount: number
  readonly cadenceMs: number
  readonly calendar?: FrameCalendarKind
  /** Per-frame stamps. Used only when there is one per frame, finite and strictly ascending. */
  readonly timesMs?: readonly number[] | null
}

function validTimes(times: readonly number[] | null | undefined, frameCount: number): readonly number[] | null {
  if (!times || times.length !== frameCount || frameCount < 1) return null
  for (let i = 0; i < times.length; i++) {
    if (!Number.isFinite(times[i]!)) return null
    if (i > 0 && !(times[i]! > times[i - 1]!)) return null
  }
  return times
}

export function createFrameAxis(input: FrameAxisInput): FrameAxis {
  const calendar = input.calendar ?? 'standard'
  const frameCount = Math.max(1, Math.floor(input.frameCount))
  const timesMs = validTimes(input.timesMs, frameCount)
  const startMs = timesMs ? timesMs[0]! : input.startMs
  let cadenceMs = input.cadenceMs
  if (!(cadenceMs > 0) && timesMs && timesMs.length > 1) {
    cadenceMs = (timesMs[timesMs.length - 1]! - timesMs[0]!) / (timesMs.length - 1)
  }
  const step = stepForCadence(cadenceMs, calendar)
  const real = civilOf(startMs)
  // A real date the model calendar lacks (31 March on a 360-day calendar)
  // is that month's last model day.
  const origin: Civil = { ...real, d: Math.min(real.d, monthDays(calendar, real.y, real.m)) }
  return { startMs, frameCount, cadenceMs, calendar, step, precision: precisionForStep(step), timesMs, origin }
}

/**
 * Stamp of any whole frame number, including ones past either end (the
 * exclusive end is frame `frameCount`). Not clamped: callers clamp.
 */
function stampAt(axis: FrameAxis, frame: number): number {
  const times = axis.timesMs
  if (times) {
    const n = times.length
    if (frame >= 0 && frame < n) return times[frame]!
    const gap = n > 1 ? (frame < 0 ? times[1]! - times[0]! : times[n - 1]! - times[n - 2]!) : axis.cadenceMs
    return frame < 0 ? times[0]! + frame * gap : times[n - 1]! + (frame - n + 1) * gap
  }
  const { step, origin, calendar } = axis
  if (step.kind === 'months') {
    const total = origin.m + frame * step.months
    const y = origin.y + floorDiv(total, 12)
    const m = total - floorDiv(total, 12) * 12
    return realMs({ y, m, d: Math.min(origin.d, monthDays(calendar, y, m)), tod: origin.tod })
  }
  if (calendar === 'standard') return axis.startMs + frame * step.ms
  // A fixed step in a model calendar: count in model time, show on the real one.
  const at = modelDayNumber(calendar, origin.y, origin.m, origin.d) * DAY + origin.tod + frame * step.ms
  const day = floorDiv(at, DAY)
  return realMs({ ...modelDateOfDay(calendar, day), tod: at - day * DAY })
}

function clampFrame(axis: FrameAxis, frame: number): number {
  if (!Number.isFinite(frame)) return 0
  return Math.max(0, Math.min(axis.frameCount - 1, Math.floor(frame)))
}

/** Stamp of a frame, clamped to the axis. */
export function frameTimeMs(axis: FrameAxis, frame: number): number {
  return stampAt(axis, clampFrame(axis, frame))
}

/** Stamp of the last frame. */
export function lastFrameTimeMs(axis: FrameAxis): number {
  return stampAt(axis, axis.frameCount - 1)
}

/** Exclusive end of the axis: where a frame after the last would be stamped. */
export function axisEndMs(axis: FrameAxis): number {
  return stampAt(axis, axis.frameCount)
}

/**
 * The largest whole frame number (unclamped) whose stamp is at or before
 * `ms`. Starts from the mean step's guess and walks: on a calendar axis
 * the guess is off by at most one frame over any length of axis.
 */
function floorFrame(axis: FrameAxis, ms: number): number {
  const mean = axis.cadenceMs > 0 ? axis.cadenceMs : 1
  let i = Math.floor((ms - axis.startMs) / mean)
  if (!Number.isFinite(i)) return 0
  for (let guard = 0; guard < 64 && stampAt(axis, i) > ms; guard++) i--
  for (let guard = 0; guard < 64 && stampAt(axis, i + 1) <= ms; guard++) i++
  return i
}

/** The period key a coarse label names: the year, or the month counted from year 0. */
function periodKey(ms: number, precision: FramePrecision): number {
  const date = new Date(ms)
  return precision === 'year' ? date.getUTCFullYear() : date.getUTCFullYear() * 12 + date.getUTCMonth()
}

/**
 * The frame a date belongs to, clamped to the axis: the frame whose year
 * or month (as its label names it) contains the date on a yearly or
 * monthly axis, the nearest frame on a finer one.
 */
export function frameAtTimeMs(axis: FrameAxis, ms: number): number {
  if (!Number.isFinite(ms)) return 0
  if (axis.precision === 'year' || axis.precision === 'month') {
    const key = periodKey(ms, axis.precision)
    // Binary search for the last frame whose label's period starts at or
    // before the date's: labels are monotonic along the axis.
    let lo = 0
    let hi = axis.frameCount - 1
    if (periodKey(stampAt(axis, 0), axis.precision) > key) return 0
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1
      if (periodKey(stampAt(axis, mid), axis.precision) <= key) lo = mid
      else hi = mid - 1
    }
    return lo
  }
  if (!axis.timesMs && axis.step.kind === 'fixed' && axis.calendar === 'standard') {
    return clampFrame(axis, Math.round((ms - axis.startMs) / axis.step.ms))
  }
  return nearestFrameAtTimeMs(axis, ms)
}

/**
 * The frame whose stamp is nearest an instant, clamped, whatever the
 * precision. For a time that came from a straight line through the axis
 * (a pin graph's slot, spread evenly from first stamp to last) rather
 * than from a calendar: such a time sits a day or two off its frame's
 * stamp on a monthly axis, which can be the month before.
 */
export function nearestFrameAtTimeMs(axis: FrameAxis, ms: number): number {
  if (!Number.isFinite(ms)) return 0
  const i = floorFrame(axis, ms)
  return clampFrame(axis, ms - stampAt(axis, i) < stampAt(axis, i + 1) - ms ? i : i + 1)
}

/** Frame position (fractional, unclamped) of an instant, between the stamps around it. */
export function frameFloatAtTimeMs(axis: FrameAxis, ms: number): number {
  if (!Number.isFinite(ms)) return 0
  const i = floorFrame(axis, ms)
  const a = stampAt(axis, i)
  const b = stampAt(axis, i + 1)
  return b > a ? i + (ms - a) / (b - a) : i
}

/** The instant at a fractional frame position (unclamped): the inverse of {@link frameFloatAtTimeMs}. */
export function timeAtFrameFloat(axis: FrameAxis, frame: number): number {
  if (!Number.isFinite(frame)) return axis.startMs
  const i = Math.floor(frame)
  const a = stampAt(axis, i)
  return frame === i ? a : a + (frame - i) * (stampAt(axis, i + 1) - a)
}

// ---------------------------------------------------------------------------
// Labelled periods (keeping two streams on one date)
// ---------------------------------------------------------------------------

/**
 * Where the stretch of time frame `frame` speaks for begins, unclamped:
 * on a yearly axis 1 January of the year its label names, on a monthly
 * one the 1st of the month; on a finer axis halfway from the previous
 * stamp, since a date there belongs to the nearest frame. The period runs
 * to the next frame's start. {@link frameAtTimeMs} picks frames by the
 * same rule, so a date inside frame i's period always seeks to frame i.
 */
export function periodStartMs(axis: FrameAxis, frame: number): number {
  const stamp = stampAt(axis, frame)
  if (axis.precision === 'year' || axis.precision === 'month') {
    const date = new Date(stamp)
    return utcMs(date.getUTCFullYear(), axis.precision === 'year' ? 0 : date.getUTCMonth(), 1, 0)
  }
  return (stampAt(axis, frame - 1) + stamp) / 2
}

/** Where a date sits relative to the periods an axis's frames cover. */
export type PeriodPosition = 'before' | 'inside' | 'after'

/** Before frame 0's period, inside one of the frames', or at or after the last one's end. */
export function periodPosition(axis: FrameAxis, ms: number): PeriodPosition {
  if (ms < periodStartMs(axis, 0)) return 'before'
  if (ms >= periodStartMs(axis, axis.frameCount)) return 'after'
  return 'inside'
}

/**
 * A date as a fractional frame position on the labelled periods,
 * unclamped: frame i's whole part for any date in i's period, with the
 * fraction saying how far through the period it is. Continuous and
 * rising, so a stream kept on another's date moves smoothly rather than
 * in steps, and the whole part is always the frame whose label names
 * the date (not the frame whose stamp precedes it: an annual frame
 * stamped on 2 July still owns January to June).
 */
export function periodFrameFloat(axis: FrameAxis, ms: number): number {
  if (!Number.isFinite(ms)) return 0
  let i = floorFrame(axis, ms)
  for (let guard = 0; guard < 64 && periodStartMs(axis, i) > ms; guard++) i--
  for (let guard = 0; guard < 64 && periodStartMs(axis, i + 1) <= ms; guard++) i++
  const a = periodStartMs(axis, i)
  const b = periodStartMs(axis, i + 1)
  return b > a ? i + (ms - a) / (b - a) : i
}

/** The date at a fractional position on the labelled periods: the inverse of {@link periodFrameFloat}. */
export function timeAtPeriodFrameFloat(axis: FrameAxis, frame: number): number {
  if (!Number.isFinite(frame)) return periodStartMs(axis, 0)
  const i = Math.floor(frame)
  const a = periodStartMs(axis, i)
  return frame === i ? a : a + (frame - i) * (periodStartMs(axis, i + 1) - a)
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/**
 * A frame's instant as people read it, in UTC, at the axis's precision:
 * `2075`, `Feb 2024`, `Apr 10, 2026`, `Aug 18, 14:00`. The forms are the
 * date track's, which the readout over the globe shares. `locale` is
 * en-US for what goes into files, citations and methods (English, like
 * every CSV row); the date track and the sibling notice pass the active
 * language (AYNI, 2026-10-06: dates follow the language).
 */
export function formatFrameLabel(ms: number, precision: FramePrecision, locale = 'en-US'): string {
  const date = new Date(ms)
  switch (precision) {
    case 'year':
      return String(date.getUTCFullYear())
    case 'month':
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' })
    case 'day':
      return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    case 'minute':
    case 'second':
      return date.toLocaleString(locale, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...(precision === 'second' ? { second: '2-digit' as const } : {}),
        hour12: false,
        timeZone: 'UTC',
      })
  }
}

function pad(n: number, width = 2): string {
  const s = String(Math.abs(n)).padStart(width, '0')
  return n < 0 ? '-' + s : s
}

/**
 * A frame's instant for a file or a citation, ISO 8601 cut to the axis's
 * precision: `1991`, `1991-01`, `1991-01-02`, `1991-01-02T08:00Z`,
 * `1991-01-02T08:00:05Z`. A spreadsheet reads all of them as dates, and
 * none claims a precision the data does not have.
 */
export function formatFrameIso(ms: number, precision: FramePrecision): string {
  const date = new Date(ms)
  const ymd = [pad(date.getUTCFullYear(), 4), pad(date.getUTCMonth() + 1), pad(date.getUTCDate())]
  switch (precision) {
    case 'year':
      return ymd[0]!
    case 'month':
      return ymd[0] + '-' + ymd[1]
    case 'day':
      return ymd.join('-')
    case 'minute':
      return ymd.join('-') + 'T' + pad(date.getUTCHours()) + ':' + pad(date.getUTCMinutes()) + 'Z'
    case 'second':
      return ymd.join('-') + 'T' + pad(date.getUTCHours()) + ':' + pad(date.getUTCMinutes()) + ':' + pad(date.getUTCSeconds()) + 'Z'
  }
}
